import React, { Fragment, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
export interface RibbonItem {
  label: string;
  icon: React.ElementType;
  prompt: string;
}
interface CategoryRibbonProps {
  items: RibbonItem[];
  onSelect: (item: RibbonItem) => void;
}

/** Gentle settle — no overshoot, no bounce. */
const GLIDE = {
  type: 'spring',
  stiffness: 420,
  damping: 40,
  mass: 0.6
} as const;
const EASE = [0.23, 1, 0.32, 1] as const;
type Box = {
  left: number;
  width: number;
};

/**
 * The Discover category ribbon.
 *
 * There is exactly ONE hover surface for the whole ribbon. It is measured
 * against the segment under the cursor and glides there — it never exists
 * in two places at once, so moving between categories can't leave a ghost
 * behind on the previous one. Leaving the ribbon fades it out in place;
 * re-entering fades it in on the segment you entered without sliding.
 */
export function CategoryRibbon({
  items,
  onSelect
}: CategoryRibbonProps) {
  const reduce = useReducedMotion();
  const [hovered, setHovered] = useState<number | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [visible, setVisible] = useState(false);
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // True for the first placement after entering, so the surface appears on
  // the segment you entered instead of sliding over from the last one.
  const [instant, setInstant] = useState(true);
  const show = (idx: number) => {
    const el = btnRefs.current[idx];
    if (!el) return;
    setInstant(!visible);
    setHovered(idx);
    setBox({
      left: el.offsetLeft,
      width: el.offsetWidth
    });
    setVisible(true);
  };
  const hide = () => {
    setHovered(null);
    setVisible(false);
  };
  return <div onPointerLeave={hide} className="relative flex items-stretch w-full p-1.5 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-1985" data-mp="2yin6xmqwtyq7">
      {/* The single gliding surface. */}
      {box && <motion.span aria-hidden className="pointer-events-none absolute top-1.5 bottom-1.5 left-0 rounded-xl p-px bg-gradient-to-b from-primary-indigo/25 via-ai-purple/20 to-primary-indigo/25 shadow-[0_6px_16px_-10px_rgba(79,70,229,0.45)]" initial={false} animate={{
      x: box.left,
      width: box.width,
      opacity: visible ? 1 : 0
    }} transition={{
      // While hidden, jump straight to the new segment so re-entry
      // never slides in from the last place you were.
      x: reduce || instant ? {
        duration: 0
      } : GLIDE,
      width: reduce || instant ? {
        duration: 0
      } : GLIDE,
      opacity: {
        duration: 0.2,
        ease: EASE
      }
    }} data-id="element-1986" data-mp="1n2yjyvdnozpe">
          <span className="relative block w-full h-full overflow-hidden rounded-[11px] bg-gradient-to-b from-indigo-50 to-[#F7F5FF]" data-id="element-1987" data-mp="2s47tw3ppai83">
            <span className="absolute bottom-0 left-1/2 h-[2px] w-8 -ml-4 rounded-full bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-1988" data-mp="ud55hl95ij55" />
          </span>
        </motion.span>}

      {items.map((item, idx) => {
      const Icon = item.icon;
      const on = hovered === idx;
      const dividerHidden = hovered === idx || hovered === idx + 1;
      return <Fragment key={item.label} data-id="element-1989" data-mp="2hzhpzsaql418" data-mpi={idx}>
            <button ref={el => {
          btnRefs.current[idx] = el;
        }} onPointerEnter={() => show(idx)} onFocus={() => show(idx)} onBlur={hide} onClick={() => onSelect(item)} className="relative flex-1 min-w-0 flex flex-col items-center justify-center gap-2 px-1 py-2.5 rounded-xl focus-visible:outline-none" data-id="element-1990" data-mp="2i0jb6plgv6wo">
              <Icon className={`relative w-[18px] h-[18px] transition-[color,transform] duration-200 ease-out ${on ? 'text-primary-indigo -translate-y-0.5' : 'text-slate-400 translate-y-0'}`} strokeWidth={1.5} data-id="element-1991" data-mp="1yed8myh5ys1d" />
              <span className={`relative text-[10px] font-semibold uppercase tracking-[0.12em] truncate max-w-full transition-colors duration-200 ease-out ${on ? 'text-primary-indigo' : 'text-slate-500'}`} data-id="element-1992" data-mp="3eoz1wp0bbmbd">
                {item.label}
              </span>
            </button>
            {idx < items.length - 1 && <div aria-hidden className={`relative w-px self-center h-7 bg-slate-100 flex-shrink-0 transition-opacity duration-200 ease-out ${dividerHidden ? 'opacity-0' : 'opacity-100'}`} data-id="element-1993" data-mp="3i87oa97nm1hr" />}
          </Fragment>;
    })}
    </div>;
}